import {
    hostPpq,
    meterBpm,
    meterPlaybackState,
    meterTimeSignature,
} from "@/client/core/audio/host-signals.audio";
import type { LabsElementaryGraph } from "@/client/core/platform-types/labs.api";
import { createGlobalClockMeterNode, createMeterNode } from "@/client/core/platform-types/labs.api";
import { el } from "@splice/elemaudio-core";

export const createSilentGraph = (): LabsElementaryGraph => {
    return { nodes: [0, 0], resourceIDs: [] };
};

/**
 * Sums two graphs into the one stereo pair the host renders.
 *
 * The platform contract carries exactly one `LabsElementaryGraph` per
 * namespace, so anything that wants to be heard alongside an app has to be
 * mixed in rather than rendered separately. Node keys are preserved by
 * `el.add`, so a voice that is already sounding keeps its identity — and its
 * state — across a re-render.
 */
export const mixGraphs = (a: LabsElementaryGraph, b: LabsElementaryGraph): LabsElementaryGraph => {
    const resourceIDs = [...a.resourceIDs];
    for (const id of b.resourceIDs) {
        if (!resourceIDs.some((existing) => existing.value === id.value)) {
            resourceIDs.push(id);
        }
    }
    return {
        nodes: [el.add(a.nodes[0], b.nodes[0]), el.add(a.nodes[1], b.nodes[1])],
        resourceIDs,
    };
};

export type SamplePlaybackMode = "trigger" | "gate";

export const createSimpleAudioBufferPlaybackGraph = (() => {
    const kSimplePlaybackKey = "simple-playback";
    let nextTrigger = 1;
    return (
        resourceIdentifier: string,
        numChannels: number,
        startOffset_samples = 0,
    ): LabsElementaryGraph => {
        const trigger = nextTrigger++; // need a rising edge for the trigger to work
        // TODO swap this out for DAW sync'd graph, and provide option
        const nodes = el.mc.sample(
            {
                path: resourceIdentifier,
                mode: "trigger",
                channels: numChannels,
                startOffset: startOffset_samples,
                key: kSimplePlaybackKey,
            },
            trigger,
        );
        const rightIndex = nodes.length >= 2 ? 1 : 0;
        return {
            nodes: [nodes[0], nodes[rightIndex]],
            resourceIDs: [{ value: resourceIdentifier, type: "wav" }],
        };
    };
})();

/**
 * Calculate the duration in terms of musical beats given bpm and duration.
 * Can return the nearest power of two beats greater than or equal to the
 * length of the provided sample. This allows us to support playback where
 * loops that are not exactly 1, 2, 4, etc. beats in length
 * are padded with silence until the next power of two bar arrives (e.g.
 * a 3 bar loop will be looped for 4 bars where the last bar is silence).
 */
export const playbackDurationInBeats = (options: {
    duration_s: number;
    bpm: number;
    useNearestPowerOfTwo?: boolean;
}): number => {
    const duration_beats = (options.duration_s / 60) * options.bpm;
    if (!options.useNearestPowerOfTwo) {
        return duration_beats;
    }

    const roundedBeatDuration = Math.round(duration_beats);
    const nearestPowerOfTwo = [1, 2, 4, 8, 16, 32, 64, 128, 256].find((powerOfTwo) => {
        return roundedBeatDuration <= powerOfTwo;
    });
    return nearestPowerOfTwo ?? 256;
};

interface LoopBoundsOneShot {
    isOneShot: true;
    repeatMultiplier: number;
    beatsPerBar: number;
}

interface LoopBoundsLoop {
    isOneShot: false;
    duration_s: number;
    bpm: number;
    timestretchMultiplier: number;
    beatsPerBar: number;
}

export type LoopBoundsParams = LoopBoundsOneShot | LoopBoundsLoop;

export interface LoopBounds {
    startBeat: number;
    stopBeat: number;
}

export const createLoopBounds = (params: LoopBoundsParams): LoopBounds => {
    if (params.isOneShot) {
        const stopBeat = params.beatsPerBar * (1.0 / params.repeatMultiplier);
        return { startBeat: 0, stopBeat };
    }

    const multiplier = Math.max(params.timestretchMultiplier, Number.EPSILON);
    const stopBeat =
        playbackDurationInBeats({
            duration_s: params.duration_s,
            bpm: params.bpm,
            useNearestPowerOfTwo: params.beatsPerBar === 4,
        }) / multiplier;

    return { startBeat: 0, stopBeat };
};

export const kTransportBpmMeterID = "transport-bpm";
export const kTransportPlaybackMeterID = "transport-playback";
export const kTransportTimeSigNumeratorMeterID = "transport-timeSig-numerator";
export const kTransportTimeSigDenominatorMeterID = "transport-timeSig-denominator";

export const injectTransportMeterNodes = (graph: LabsElementaryGraph) => {
    const playbackNode = meterPlaybackState();
    const gatedPpq = el.select(playbackNode, hostPpq(), 0);
    const ppqMeter = createGlobalClockMeterNode(0, gatedPpq);
    const bpmMeter = createMeterNode(kTransportBpmMeterID, "", meterBpm());
    const playbackMeter = createMeterNode(kTransportPlaybackMeterID, "", playbackNode);
    const [tsNum, tsDenom] = meterTimeSignature();
    const tsNumMeter = createMeterNode(kTransportTimeSigNumeratorMeterID, "", tsNum);
    const tsDenomMeter = createMeterNode(kTransportTimeSigDenominatorMeterID, "", tsDenom);

    graph.nodes[0] = el.add(
        graph.nodes[0],
        ppqMeter,
        bpmMeter,
        playbackMeter,
        tsNumMeter,
        tsDenomMeter,
    );
};
